import { Plus } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import RoleList from "../../components/JobDisplay/RoleList.js";
import PageHeader from "../../components/Navigation/PageHeader.js";

import Button from "../../lib/ui/components/button/Button";
import Dropdown from "../../lib/ui/components/dropdown/Dropdown";


// Store
import { useStore } from "../../store/index.js";
import { ATS_INTEGRATION, JOBS } from "../../lib/urls.js";

function RolePage() {
  const navigate = useNavigate();
  const setJobs = useStore((state) => state.setJobs);
  const token = useStore((state) => state.token);
  const { id: companyId } = useStore((state) => state.company);

  // State for jobs data
  const [jobs, setJobsLocal] = useState([]);
  const [loading, setLoading] = useState(true);

  // ATS Integration State
  const [atsLoading, setAtsLoading] = useState(false);

  const createRoleHandler = () => {
    navigate("/jobs/new");
  };

  // Fetch jobs on initial load
  useEffect(() => {
    if (companyId) {
      fetchJobs();
    }
  }, [companyId]);

  // ATS Integration Functions
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await fetch(JOBS(companyId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch job list");
      }

      const data = await response.json();
      setJobsLocal(data);
      setJobs(data); // Also update store for backward compatibility
    } catch (error) {
      console.error("Error fetching job profile list:", error);
      toast.error("Failed to fetch jobs. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh function that doesn't show loading state
  const silentRefresh = async () => {
    try {
      const response = await fetch(JOBS(companyId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch job list");
      }

      const data = await response.json();
      setJobsLocal(data);
      setJobs(data); // Also update store for backward compatibility
    } catch (error) {
      console.error("Error silently refreshing jobs:", error);
      // Don't show toast for silent refresh errors
    }
  };

  const handleClickATSIntegration = async () => {
    setAtsLoading(true);

    try {
      const response = await fetch(ATS_INTEGRATION(companyId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch ATS jobs");
      }

      const data = await response.json();
      console.log(`Jobs Count: ${data.count}`);
      console.log(`Jobs: ${JSON.stringify(data.data)}`);
      console.log(`Last Sync: ${data.last_sync_time}`);

      // After ATS integration is complete, refresh the jobs list
      await fetchJobs();

      toast.success("ATS integration completed successfully");
    } catch (error) {
      console.error("Error in ATS integration:", error);
      toast.error("Failed to process ATS integration. Please try again.");
    } finally {
      setAtsLoading(false);
    }
  };

  // Action Dropdown Items (will add more actions later)
  const dropdownActionItems = [
    {
      label: "Update from ATS",
      onClick: handleClickATSIntegration,
      disabled: atsLoading,
      variant: "normal",
    },
  ];

  return (
    <div className="w-full bg-gray-100 min-h-screen">
      <PageHeader
        title="Roles"
        actions={
          <>
            <Button
              variant="primary"
              leftIcon={<Plus size="1em" />}
              onClick={createRoleHandler}
              id="create-role-button"
            >
              Create a Role
            </Button>
            <Dropdown items={dropdownActionItems} />
          </>
        }
      />
      <div className="w-full mt-4">
        <div className="mx-4">
          <div className="w-full flex justify-center">
            <div className="w-[98%] border border-gray-300 rounded-lg bg-white">
              <RoleList jobs={jobs} loading={loading} onRefresh={silentRefresh} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RolePage;
